import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'

import type { ToggleCheckApplicablePayload, SfJQuery } from '../../src/types'
import api from '../../src/application/communication/sections/communication/api/check'
import sanitizer from '../../src//adapters/sanitizer'
import { SfLog } from '../../src/adapters/logger'
import { anyCheckinFromOtherUsers } from '../../src/application/communication/utils'
import { updateLedgerCheckListFilter } from '@app/application/ledger-check-list/api'
import type { FilterType } from '@app/application/ledger-check-list/types'
import {
  DEFAULT_FILTER,
  isValidFilterType,
} from '@app/application/ledger-check-list/types'

export default class extends Controller {
  static targets = [
    'addUserBtn',
    'addUserForm',
    'userSelect',
    'allChecksFilter',
    'myChecksFilter',
    'openChecksFilter',
    'allChecksCount',
    'myChecksCount',
    'openChecksCount',
    'emptyStateOpen',
    'emptyStateMyOpen',
  ]

  static values = {
    ledgerCheckListId: Number,
    currentFilter: String,
    companyWorkflowId: Number,
  }

  declare ledgerCheckListIdValue: number
  declare currentFilterValue: string
  declare companyWorkflowIdValue: number

  // Declare target getters for TypeScript
  declare readonly hasUserSelectTarget: boolean

  // Add property for select2
  private select2Instance: SfJQuery | null = null

  // Event listener property
  private performToggleCheckApplicableListener!: (
    payload: ToggleCheckApplicablePayload
  ) => void

  // Define target types with definite assignment
  private addUserBtnTarget!: HTMLElement
  private addUserFormTarget!: HTMLElement
  private userSelectTarget!: HTMLSelectElement

  private allChecksFilterTarget!: HTMLButtonElement
  private myChecksFilterTarget!: HTMLButtonElement
  private openChecksFilterTarget!: HTMLButtonElement
  private allFilterTargets: HTMLButtonElement[] = []

  private allChecksCountTarget!: HTMLElement
  private myChecksCountTarget!: HTMLElement
  private openChecksCountTarget!: HTMLElement

  private emptyStateOpenTarget!: HTMLElement
  private emptyStateMyOpenTarget!: HTMLElement

  connect(): void {
    this.initFilterCounts()
    this.initializeSelect2()

    this.performToggleCheckApplicableListener = (
      payload: ToggleCheckApplicablePayload
    ): void => {
      void this.performToggleCheckApplicable(payload)
    }

    window.eventHub.$on(
      'confirm-check-toggle-applicable',
      this.performToggleCheckApplicableListener
    )

    this.allFilterTargets = [
      this.allChecksFilterTarget,
      this.myChecksFilterTarget,
      this.openChecksFilterTarget,
    ]

    if (isValidFilterType(this.currentFilterValue)) {
      void this.filterChecksByType(this.currentFilterValue)
    }

    this.element.classList.remove('tw-hidden')

    this.scrollToAndHighlightAnchoredCheck()
  }

  private scrollToAndHighlightAnchoredCheck(): void {
    const hash = window.location.hash
    if (!hash || !hash.startsWith('#check-link-')) {
      return
    }

    const checkElement = document.querySelector(hash)
    if (!checkElement) {
      return
    }

    // Ensure the check and its container are visible (in case a filter is active)
    const container = checkElement.closest('.checkable-container')
    if (container) {
      container.classList.remove('tw-hidden')
    }
    checkElement.classList.remove('tw-hidden')

    // Scroll into view
    checkElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // Add highlight border color (border-2 and transition classes are already on the element)
    checkElement.classList.remove('tw-border-transparent')
    checkElement.classList.add('tw-border-cyan-600')

    // Remove highlight after 3 seconds with fade transition
    setTimeout(() => {
      checkElement.classList.remove('tw-border-cyan-600')
      checkElement.classList.add('tw-border-transparent')
    }, 3000)
  }

  disconnect(): void {
    this.select2Instance?.off()
    window.eventHub.$off(
      'confirm-check-toggle-applicable',
      this.performToggleCheckApplicableListener
    )
  }

  async toggleCheckApplicable(evt: Event): Promise<void> {
    const togglePayload = this.parseToggleCheckApplicablePayload(
      evt.target as HTMLElement
    )

    if (!togglePayload) {
      return
    }

    if (anyCheckinFromOtherUsers(togglePayload.checkinUserIds || [])) {
      window.modalCitizen.openModalPortal(
        'confirm-check-toggle-applicable-modal',
        {
          checkLinkId: togglePayload.checkLinkId,
          ledgerCheckListId: togglePayload.ledgerCheckListId,
        }
      )
    } else {
      await this.performToggleCheckApplicable(togglePayload)
    }
  }

  private async performToggleCheckApplicable(
    payload: ToggleCheckApplicablePayload
  ): Promise<void> {
    try {
      const { ledgerCheckListId, checkLinkId } = payload
      const response = (await api.toggleCheckApplicable(
        ledgerCheckListId,
        checkLinkId,
        this.companyWorkflowIdValue
      )) as { checkHtml?: string } | null
      if (response?.checkHtml) {
        this.toggleCheckinOnSuccess(
          checkLinkId.toString(),
          response.checkHtml,
          true
        )
        document
          .querySelectorAll(
            "[data-controller='ledger-check-list--summary'] [data-controller='ledger-check-list--progress']"
          )
          .forEach((el) => {
            el.dispatchEvent(
              new CustomEvent('ledger-check-list--progress:updateSummary')
            )
          })
      } else {
        window.location.reload()
      }
    } catch (error) {
      SfLog.error('Error toggling check not applicable status:', { error })
    }
  }

  private parseToggleCheckApplicablePayload(
    element: HTMLElement
  ): ToggleCheckApplicablePayload | undefined {
    const data = element.dataset
    const payload = data['toggleApplicablePayload'] as string
    if (!payload) {
      return
    }
    try {
      return JSON.parse(payload) as ToggleCheckApplicablePayload
    } catch (e) {
      SfLog.warn('Failed to parse data-payload for ToggleCheckApplicable', {
        extra: {
          error: e,
        },
      })
    }
  }

  private initializeSelect2(): void {
    if (!this.hasUserSelectTarget) {
      return
    }

    // Cast to our extended jQuery type
    this.select2Instance = $(this.userSelectTarget)

    this.select2Instance.on('change', (e: JQuery.ChangeEvent) => {
      const target = e.target as HTMLSelectElement
      this.submitAddUsersForm(target.value)
    })

    this.select2Instance.on('select2:close', () => {
      this.hideAddUsersForm()
    })
  }

  private showAddUsersForm(): void {
    if (!this.addUserBtnTarget || !this.addUserFormTarget) return

    this.addUserBtnTarget.style.display = 'none'
    this.addUserFormTarget.style.display = 'block'
    // Focus and open Select2
    this.select2Instance?.select2?.('open')
  }

  private hideAddUsersForm(): void {
    if (!this.addUserBtnTarget || !this.addUserFormTarget) return

    this.addUserBtnTarget.style.display = 'block'
    this.addUserFormTarget.style.display = 'none'
  }

  private submitAddUsersForm(_userId: string): void {
    if (!this.addUserBtnTarget) return

    this.addUserBtnTarget.style.display = 'none'
    const form = this.addUserFormTarget?.querySelector(
      'form'
    ) as HTMLFormElement
    if (form) {
      form.submit()
    }

    window.Silverfin.Spinner.start()
  }

  private initFilterCounts(): void {
    if (!this.allChecksCountTarget || !this.myChecksCountTarget) return

    let myOpenChecksCount = 0
    let openChecksCount = 0

    const allChecks = this.processChecks(
      DEFAULT_FILTER,
      (_check, completedOrNotApplicable, myOpenCheck) => {
        if (completedOrNotApplicable) {
          return
        }

        if (myOpenCheck) {
          myOpenChecksCount++
        }

        openChecksCount++
      }
    )

    this.allChecksCountTarget.textContent = allChecks.length.toString()
    this.myChecksCountTarget.textContent = myOpenChecksCount.toString()
    this.openChecksCountTarget.textContent = openChecksCount.toString()
  }

  public async filterChecks(event: Event): Promise<void> {
    const target = event.currentTarget as HTMLElement
    const type = (target.dataset.filter as FilterType) || DEFAULT_FILTER
    await this.filterChecksByType(type)
  }

  public async filterChecksByType(type: FilterType): Promise<void> {
    this.processChecks(type, (check, completedOrNotApplicable, myOpenCheck) => {
      const visible = this.determineCheckVisibility(
        type,
        completedOrNotApplicable,
        myOpenCheck
      )

      if (visible) {
        check.classList.remove('tw-hidden')
      } else {
        check.classList.add('tw-hidden')
      }

      this.updateContainerVisibility(check)
    })

    this.toggleOtherUserColumnsVisibility(type)

    let targetToActivate = null
    switch (type) {
      case 'my':
        targetToActivate = this.myChecksFilterTarget
        break
      case 'all':
        targetToActivate = this.allChecksFilterTarget
        break
      case 'open':
        targetToActivate = this.openChecksFilterTarget
        break
    }

    this.activateFilter(targetToActivate)
    this.allFilterTargets.forEach((filter) => {
      if (filter === targetToActivate) return

      this.inactivateFilter(filter)
    })

    await updateLedgerCheckListFilter(
      this.ledgerCheckListIdValue.toString(),
      type
    )
  }

  private processChecks(
    type: FilterType,
    callback: (
      check: HTMLElement,
      completedOrNotApplicable: boolean,
      myOpenCheck: boolean
    ) => void
  ): NodeListOf<HTMLElement> {
    const checkSelector = '[data-behaviour~="ledger-check-list-check"]'
    const checks = this.element.querySelectorAll<HTMLElement>(checkSelector)

    checks.forEach((check) => {
      const checkData = check.dataset
      const completed = checkData['completed'] === 'true'
      const notApplicable = checkData['notApplicable'] === 'true'
      const completedOrNotApplicable = completed || notApplicable

      const myOpenCheck = this.hasMyOpenCheck(check)

      callback(check, completedOrNotApplicable, myOpenCheck)
    })

    this.updateEmptyState(type)

    return checks
  }

  private determineCheckVisibility(
    type: FilterType,
    completedOrNotApplicable: boolean,
    myOpenCheck: boolean
  ): boolean {
    switch (type) {
      case 'my':
        return !completedOrNotApplicable && myOpenCheck
      case 'open':
        return !completedOrNotApplicable
      default:
        return true
    }
  }

  private toggleOtherUserColumnsVisibility(type: FilterType): void {
    // Toggle user column cells
    this.applyUserColumnVisibility(this.element, type)

    // Toggle user column headers
    const headerSelector = '[data-behaviour="ledger-check-list-user-header"]'
    const headers = this.element.querySelectorAll<HTMLElement>(headerSelector)

    headers.forEach((header) => {
      const isCurrentUser = header.dataset.currentUser === 'true'

      if (type === 'my' && !isCurrentUser) {
        header.classList.add('tw-hidden')
      } else {
        header.classList.remove('tw-hidden')
      }
    })
  }

  private activateFilter(filter: HTMLElement | null): void {
    if (!filter) return

    filter.classList.add('active')
    if (filter instanceof HTMLButtonElement) {
      filter.disabled = true
    }
  }

  private inactivateFilter(filter: HTMLElement | null): void {
    if (!filter) return

    filter.classList.remove('active')
    if (filter instanceof HTMLButtonElement) {
      filter.disabled = false
    }
  }

  public toggleCheckinOnSuccess(
    id: string,
    checkHtml: string,
    applyCurrentFilter = false
  ): void {
    const temp = document.createElement('div')
    temp.innerHTML = sanitizer(checkHtml.trim()) as string
    const newCheck = temp.firstElementChild as HTMLElement

    if (!newCheck) return

    const existingCheck = this.element.querySelector(
      `[data-behaviour~="ledger-check-list-check"][data-id="${id}"]`
    )
    if (existingCheck && existingCheck.parentNode) {
      existingCheck.parentNode.replaceChild(newCheck, existingCheck)
    }

    this.initFilterCounts()

    if (applyCurrentFilter) {
      this.applyCurrentFilter(newCheck)
    }
  }

  private currentFilterType(): FilterType {
    const activeFilter = this.allFilterTargets.find((filter) =>
      filter.classList.contains('active')
    )
    const filterType = activeFilter?.dataset.filter

    if (!filterType) {
      return DEFAULT_FILTER
    }

    if (isValidFilterType(filterType)) {
      return filterType
    }

    return DEFAULT_FILTER
  }

  private applyCurrentFilter(check: HTMLElement): void {
    const type = this.currentFilterType()
    const completed = check.dataset['completed'] === 'true'
    const notApplicable = check.dataset['notApplicable'] === 'true'
    const completedOrNotApplicable = completed || notApplicable
    const myOpenCheck = this.hasMyOpenCheck(check)
    const visible = this.determineCheckVisibility(
      type,
      completedOrNotApplicable,
      myOpenCheck
    )

    check.classList.toggle('tw-hidden', !visible)
    this.updateContainerVisibility(check)
    this.toggleOtherUserColumnsVisibility(type)
    this.updateEmptyState(type)
  }

  private hasMyOpenCheck(check: HTMLElement): boolean {
    // myCheck is true when the check is for the current user AND completed
    // myCheck is false when the check is for the current user AND NOT completed
    return (
      check.querySelectorAll(
        '[data-behaviour="ledger-check-list-user"][data-my-check="false"]'
      ).length > 0
    )
  }

  private updateContainerVisibility(check: HTMLElement): void {
    const container = check.closest('.checkable-container') as HTMLElement
    if (!container) return

    const hasVisibleChecks =
      container.querySelectorAll(
        '[data-behaviour~="ledger-check-list-check"]:not(.tw-hidden)'
      ).length > 0

    container.classList.toggle('tw-hidden', !hasVisibleChecks)
  }

  private updateEmptyState(type: FilterType): void {
    const hasVisibleContainers =
      this.element.querySelectorAll('.checkable-container:not(.tw-hidden)')
        .length > 0

    this.emptyStateMyOpenTarget.classList.add('tw-hidden')
    this.emptyStateOpenTarget.classList.add('tw-hidden')

    if (!hasVisibleContainers) {
      const target =
        type === 'my' ? this.emptyStateMyOpenTarget : this.emptyStateOpenTarget
      target.classList.remove('tw-hidden')
    }
  }

  private applyUserColumnVisibility(
    container: ParentNode,
    type: FilterType
  ): void {
    const users = container.querySelectorAll<HTMLElement>(
      '[data-behaviour="ledger-check-list-user"]'
    )

    users.forEach((user) => {
      const isCurrentUser = user.dataset.myCheck !== undefined

      if (type === 'my' && !isCurrentUser) {
        user.classList.add('tw-hidden')
      } else {
        user.classList.remove('tw-hidden')
      }
    })
  }
}
